import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  Sparkles,
  Search,
  FileCheck,
  PhoneIncoming,
  Home,
  Calculator,
  CheckCircle,
} from "lucide-react";
import SEO from "@/components/SEO";
import { buildGraph, webPageJsonLd, breadcrumbJsonLd } from "@/data/seo";

const RED = "#D41F30";

const STEPS = [
  {
    Icon: PhoneIncoming,
    title: "Get your plan",
    desc: "Answer a few quick questions and get matched with a CityWorth team built around your goals.",
  },
  {
    Icon: FileCheck,
    title: "Get pre-approved",
    desc: "Lock in your buying power so sellers take your offer seriously, usually within 24 hours.",
  },
  {
    Icon: Search,
    title: "Find your home",
    desc: "Tour homes with our local agents. Skip the noise and see the ones that actually fit you.",
  },
  {
    Icon: Home,
    title: "Close & celebrate",
    desc: "Coordinated mortgage + real estate means a smoother, faster closing. Average 21 days.",
  },
];

export default function BuyAHomePage() {
  return (
    <div className="bg-white" data-testid="buy-a-home-page">
      <SEO
        title="Buy a Home | Everything homebuying in one team"
        description="From plan to keys in 4 steps. CityWorth's all-in-one homebuying combines mortgage and real estate into one guided process. Average 21 day closes."
        path="/buy-a-home"
        jsonLd={buildGraph(
          webPageJsonLd({
            name: "Buy a Home with CityWorth",
            description: "Step-by-step homebuying process, plan, pre-approve, find, close.",
            path: "/buy-a-home",
          }),
          breadcrumbJsonLd([
            { name: "Home", path: "/" },
            { name: "Buy a Home", path: "/buy-a-home" },
          ])
        )}
      />
      {/* Hero */}
      <section className="relative pt-28 pb-20 md:pt-36 md:pb-24 overflow-hidden">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-red-50 rounded-full blur-3xl opacity-50 -translate-y-1/3 translate-x-1/3 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 bg-red-50 rounded-full px-4 py-2 mb-6">
                <Home className="w-4 h-4" style={{ color: RED }} />
                <span className="text-xs font-bold tracking-[0.18em] uppercase" style={{ color: RED }}>
                  Buy a Home
                </span>
              </div>
              <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-black text-gray-900 leading-[1.02] tracking-tight mb-6">
                Everything homebuying <span style={{ color: RED }}>in one team.</span>
              </h1>
              <p className="text-lg md:text-xl text-gray-600 leading-relaxed mb-10 max-w-2xl">
                Skip the confusion. CityWorth combines mortgage and real estate into one guided experience, so you always know what's next and never feel alone in the process.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  className="bg-[#D41F30] hover:bg-[#B91C2A] text-white rounded-full px-8 py-7 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
                  onClick={() => window.location.assign("/get-started")}
                  data-testid="buy-home-cta"
                >
                  Start my homebuying plan
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button
                  variant="outline"
                  className="border-2 border-gray-300 text-gray-700 hover:bg-gray-50 rounded-full px-8 py-7 text-lg font-semibold"
                  onClick={() => window.location.assign("/mortgage-calculator")}
                >
                  <Calculator className="mr-2 w-5 h-5" />
                  Estimate my payment
                </Button>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1564013799919-ab600027ffc6?auto=format&fit=crop&w=1200&q=80"
                  alt="Beautiful home"
                  className="w-full h-[440px] object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 md:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl mb-14">
            <div className="text-xs font-bold tracking-[0.18em] uppercase mb-3" style={{ color: RED }}>
              The process
            </div>
            <h2 className="font-display text-4xl md:text-5xl font-black text-gray-900 leading-tight">
              From "thinking about it" to "I have the keys."
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {STEPS.map((s, i) => (
              <div key={i} className="bg-white rounded-2xl border border-gray-100 p-7 shadow-sm hover:shadow-lg transition-shadow relative" data-testid={`step-${i}`}>
                <div className="absolute top-5 right-5 text-5xl font-black text-red-50 leading-none">
                  {i + 1}
                </div>
                <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center mb-5 relative z-10">
                  <s.Icon className="w-6 h-6" style={{ color: RED }} />
                </div>
                <h3 className="font-display text-xl font-bold text-gray-900 mb-2 relative z-10">{s.title}</h3>
                <p className="text-gray-600 leading-relaxed text-sm relative z-10">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <div className="text-xs font-bold tracking-[0.18em] uppercase mb-3" style={{ color: RED }}>
              The CityWorth advantage
            </div>
            <h2 className="font-display text-4xl md:text-5xl font-black text-gray-900 leading-tight mb-8">
              The all-in-one homebuying team you actually need.
            </h2>
            <ul className="space-y-4">
              {[
                "Mortgage and real estate working from the same playbook",
                "Pre-approval letters that sellers actually respect",
                "Programs for every situation: first-time, VA, FHA, conventional, USDA",
                "Concierge-style support from one team start to finish",
              ].map((w, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-1" />
                  <span className="text-lg text-gray-700">{w}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-3xl overflow-hidden shadow-xl">
            <img loading="lazy"
              src="https://images.unsplash.com/photo-1568605114967-8130f3a36994?auto=format&fit=crop&w=1200&q=80"
              alt="Couple receiving keys"
              className="w-full h-[440px] object-cover"
            />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-gradient-to-br from-red-50 to-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-[#D41F30] flex items-center justify-center mb-6">
            <Sparkles className="w-7 h-7 text-white" />
          </div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-black text-gray-900 mb-6 leading-[1.05] tracking-tight">
            Let's build your plan.
          </h2>
          <p className="text-lg md:text-xl text-gray-600 mb-10 max-w-2xl mx-auto">
            2 minutes. No credit check. No commitment. Just clarity on your path to a home.
          </p>
          <Button
            className="bg-[#D41F30] hover:bg-[#B91C2A] text-white rounded-full px-10 py-7 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
            onClick={() => window.location.assign("/get-started")}
            data-testid="buy-home-cta-bottom"
          >
            Start my homebuying plan
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>
    </div>
  );
}
